export interface ApiResponse {
  success: boolean;
  errorCode: number;
  errorDescription: string;
  /** Strukturirani detalji greške — oblik zavisi od `errorCode` (v. `AccessDeniedDetails`).
      Aditivno polje: stariji server ga ne šalje, pa potrošač mora da preživi bez njega. */
  details?: any;
}

/** Detalji uz `ErrorCodes.AccessDenied` kad je odbijanje došlo iz kataloga prava —
    server javlja KOJE pravo fali, da poruka ne bude generična. Odbijanja koja ne dolaze iz
    kataloga (nije zaposlen u lokalu, tuđi QR kod…) stižu bez ovog objekta. */
export interface AccessDeniedDetails {
  /** Naziv prava, npr. `ModifyOrder` — ujedno ključ prevoda `permission.ModifyOrder`. */
  requiredPermission?: string;
  /** Nivo na kom je provera pala: `Company`, `Organization` ili `Admin`. */
  scope?: string;
  companyID?: number;
  organizationID?: number;
}

export interface GenericItemApiResponse<T> extends ApiResponse {
  item: T;
}

export interface GenericListApiResponse<T> extends ApiResponse {
  items: T[];
}

/** Straničena lista — `total` je broj SVIH pogodaka, ne samo vraćenih.
    Opciono: stariji backend ga ne šalje, pa potrošač mora da preživi bez njega. */
export interface PagedListApiResponse<T> extends GenericListApiResponse<T> {
  total?: number;
}

export enum LoginResult {
  AccessDenied,
  InvalidUserName,
  Disabled,
  OK,
  InvalidToken,
  /** Nalog ima lični kredencijal (lozinka, social ili verifikovan telefon) — prijava PIN-om,
      kodom ili API ključem nije dozvoljena; mora e-mailom/lozinkom. */
  PersonalCredentialRequired,
  /** Prijava PIN-om: nijedan zaposleni nema taj PIN. */
  InvalidPin,
  /** Prijava PIN-om: PinToken sačuvan na uređaju više ne važi (promenjen u podešavanjima lokala). */
  PinDeviceExpired,
  /** Lozinka je tačna (ili je provajder potvrdio prijavu), ali eMail nalog nikad nije potvrdio: kod je poslat na eMail
      (korak koda, user/VerifySignIn). */
  EmailCodeSent,
  /** Nalog nema lozinku (npr. otvoren u loyalty aplikaciji) — nudi se postavljanje lozinke. */
  NoPassword,
  /** Kod je tražen prečesto ili je dnevni limit potrošen. */
  TooManyRequests,
  /** Pogrešan ili istekao kod iz eMail-a. */
  InvalidCode,
  /** Ispravka adrese na koraku koda: adresa već pripada drugom nalogu. */
  EmailTaken,
  /** Ispravka adrese na koraku koda: adresa nije ispravan eMail. */
  InvalidEmail
}

/** Ishod registracije naloga (`user/register`). */
export enum RegisterResult {
  OK,
  /** eMail adresa je već registrovana. */
  MailAlreadyRegistered,
  /** Podaci ne prolaze serversku validaciju (eMail/ime/lozinka). */
  InvalidData,
  /** reCAPTCHA provera nije prošla. */
  CaptchaFailed,
  /** Kod je poslat na eMail; nalog se otvara tek posle koda (user/ConfirmSignUp). */
  CodeSent,
  /** Kod je tražen prečesto ili je dnevni limit potrošen. */
  TooManyRequests,
  /** Sve ostalo — server je odbio registraciju. */
  Failed
}

export enum ErrorCodes {
  ProductNotFound = 1,
  AccessDenied = 2,
  InvalidCompany = 3,
  SubscriptionExpired = 4,
  InvalidOperation = 5,
  InvalidToken = 6,
  UserNotFound = 7,
  InvalidResetPasswordToken = 8,
  InvalidPassword = 9,
  InvalidUserNamePassword = 10,
  InvalidBundle = 11,
  UserDisabled = 12,
  CompanyAlreadyRegistered = 13,
  CompanyNotFound = 14,
  MailAlreadyRegistered = 15,
  InvalidApiKey = 16,
  InvalidInvoice = 17,
  AdminOnly = 18,
  NotLoggedIn = 19,
  InvoiceNotFound = 20,
  AlreadyExtended = 21,
  InvalidProduct = 22,
  FileSizeTooBig = 23,
  InvalidSuggestion = 24,
  InvalidOrder = 25,
  InvalidModifier = 26,
  InvalidQRCode = 29,
  QRCodeTaken = 30,
  TooManyGlobalQRCodes = 31,
  InvalidIPAddress = 32,

  InvalidSession = 38,
  NoResponseFromPOS = 42,
  InvalidSigniture = 43,
  InvalidPrice = 44,
  InvalidCode = 45,
  InvalidAmount = 53,
  POSNotConnected = 54,
  Exists = 57,
  CaptchaFailed = 61,
  PersonalCredentialRequired = 62,
  TooManyRequests = 66,
  AccountWithoutPassword = 67,
  EmailVerificationRequired = 68,
  /** Usluga nije aktivna za lokal (nema pretplatu); `details.serviceCode` nosi kod usluge. */
  ServiceNotActive = 69
}
